import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { NC_COUNTIES } from "@/lib/ncCounties";
import { useI18n } from "@/context/I18nContext";
import { joinText, areaLabel } from "@/lib/attorneyI18n";
import { toast } from "sonner";
import { Scale, CheckCircle2, Loader2, MapPin, Star } from "lucide-react";

const Field = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
const areaText = (lang, area) => areaLabel(lang, area).toLowerCase().replace("landlord/tenant", "landlord-tenant");

const CountySelect = ({ value, onChange, L, testid }) => (
  <select value={value} onChange={onChange} data-testid={testid} className="h-10 w-full rounded-sm border border-input bg-background px-3 text-sm">
    <option value="">{L.selectCounty}</option>
    {NC_COUNTIES.map((c) => <option key={c.slug} value={c.name}>{L.countyName(c.name)}</option>)}
  </select>
);

const FeaturedOffer = ({ county: initial, email, L }) => {
  const navigate = useNavigate();
  const [county, setCounty] = useState(initial || "");
  const [av, setAv] = useState(null);
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    if (!county) { setAv(null); return; }
    api.get("/featured/availability", { params: { county } }).then(({ data }) => setAv(data)).catch(() => setAv(null));
  }, [county]);
  if (hidden) return null;
  return (
    <div className="grid-card p-6 mt-6 border-l-4 border-l-accent" data-testid="join-featured-offer">
      <p className="overline text-accent mb-2 flex items-center gap-2"><Star size={13} /> {L.offOver}</p>
      <p className="font-serif text-2xl text-foreground mb-2" data-testid="join-offer-title">{county ? L.offH(county) : L.offPick}</p>
      <p className="text-sm text-muted-foreground mb-4">{L.offB(av?.price ?? 29)}</p>
      <div className="max-w-xs mb-4"><CountySelect value={county} onChange={(e) => setCounty(e.target.value)} L={L} testid="join-offer-county" /></div>
      {av && <p className="text-xs font-semibold text-muted-foreground mb-4" data-testid="join-offer-open">{av.open > 0 ? L.offOpen(av.open, av.slots) : L.offFull}</p>}
      <div className="flex flex-wrap items-center gap-3">
        <Button className="rounded-sm gap-2" disabled={!county || (av && av.open === 0)} data-testid="join-offer-btn"
          onClick={() => navigate(`/featured?county=${encodeURIComponent(county)}`)}><Star size={14} /> {L.offBtn(av?.price ?? 29)}</Button>
        <Button variant="ghost" onClick={() => setHidden(true)} data-testid="join-offer-skip">{L.offSkip}</Button>
      </div>
      <p className="text-xs text-muted-foreground mt-3">{L.offNote(email)}</p>
    </div>
  );
};

const Done = ({ title, body, L, offer }) => (
  <>
    <div className="grid-card p-8 text-center" data-testid="join-done">
      <CheckCircle2 className="mx-auto text-emerald-500 mb-3" size={36} />
      <p className="font-serif text-2xl text-foreground mb-2">{title}</p>
      <p className="text-muted-foreground">{body}</p>
      <Link to="/find-attorney" className="inline-block mt-5 text-accent font-semibold hover:underline">{L.seeDir}</Link>
    </div>
    {offer}
  </>
);

const ClaimForm = ({ a, email, area, L, lang, onDone }) => {
  const [f, setF] = useState({ claimant_email: email, phone: a.phone || "", firm: a.firm || "", website: a.website || "", bio: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/directory/attorneys/${a.id}/claim`, f); onDone((a.counties || [])[0] || ""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || L.claimErr); }
    finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="grid-card p-8" data-testid="join-claim">
      <p className="overline text-accent mb-2">{L.found}</p>
      <p className="font-serif text-3xl text-foreground">{a.name}</p>
      <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1 mb-6"><MapPin size={13} />{[a.firm, a.city, (a.counties || []).map(L.countyName).join(", ")].filter(Boolean).join(" · ")}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={L.yourEmail}><Input value={f.claimant_email} onChange={set("claimant_email")} data-testid="join-claim-email" /></Field>
        <Field label={L.phone}><Input value={f.phone} onChange={set("phone")} data-testid="join-claim-phone" /></Field>
        <Field label={L.firm}><Input value={f.firm} onChange={set("firm")} /></Field>
        <Field label={L.website}><Input value={f.website} onChange={set("website")} /></Field>
      </div>
      <div className="mt-4"><Field label={L.bio}><Textarea rows={3} value={f.bio} onChange={set("bio")} placeholder={L.bioPh(areaText(lang, area))} /></Field></div>
      <Button onClick={submit} disabled={busy || !f.claimant_email} className="rounded-sm mt-6 gap-2" data-testid="join-claim-submit">
        {busy && <Loader2 size={16} className="animate-spin" />} {L.claim}
      </Button>
      <p className="text-xs text-muted-foreground mt-3">{L.claimNote}</p>
    </div>
  );
};

const NewListingForm = ({ email, area, L, lang, onDone }) => {
  const [f, setF] = useState({ name: "", firm: "", city: "", county: "", phone: "", email, website: "", bio: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/directory/attorneys/request", { ...f, counties: f.county ? [f.county] : [], practice_areas: [area] });
      onDone(f.county);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || L.newErr); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid-card p-8" data-testid="join-new">
      <p className="overline text-accent mb-2">{L.newOver}</p>
      <p className="text-muted-foreground mb-6">{L.newIntro(lang === "es" ? areaText(lang, area) : areaLabel(lang, area))}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={L.name}><Input value={f.name} onChange={set("name")} data-testid="join-new-name" /></Field>
        <Field label={L.email}><Input value={f.email} onChange={set("email")} data-testid="join-new-email" /></Field>
        <Field label={L.firm}><Input value={f.firm} onChange={set("firm")} /></Field>
        <Field label={L.phone}><Input value={f.phone} onChange={set("phone")} /></Field>
        <Field label={L.city}><Input value={f.city} onChange={set("city")} /></Field>
        <Field label={L.county}><CountySelect value={f.county} onChange={set("county")} L={L} testid="join-new-county" /></Field>
      </div>
      <Button onClick={submit} disabled={busy || !f.name.trim() || !f.email.trim()} className="rounded-sm mt-6 gap-2" data-testid="join-new-submit">
        {busy && <Loader2 size={16} className="animate-spin" />} {L.submit}
      </Button>
    </div>
  );
};

const LangToggle = ({ lang, setLang }) => (
  <div className="inline-flex rounded-sm border border-border overflow-hidden text-xs font-semibold mb-6" data-testid="join-lang-toggle">
    {[["en", "English"], ["es", "Español"]].map(([k, label]) => (
      <button key={k} type="button" onClick={() => setLang(k)} data-testid={`join-lang-${k}`}
        className={`px-3 py-1.5 transition-colors ${lang === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>{label}</button>
    ))}
  </div>
);

export default function JoinDirectory() {
  const { token } = useParams();
  const { lang: ctxLang } = useI18n();
  const [lang, setLang] = useState(ctxLang === "es" ? "es" : "en");
  const L = joinText(lang);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    api.get(`/invites/join/${token}`).then(({ data }) => { setInfo(data); setLang(data.lang === "es" ? "es" : "en"); })
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "invalid"));
  }, [token]);
  const area = info?.area || "Landlord/Tenant";
  const joined = info && ["claimed", "listed"].includes(info.status);
  const reviewing = info && ["listing_requested", "claim_pending"].includes(info.status);
  const offer = (county) => <FeaturedOffer county={county} email={info.email} L={L} />;
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1 w-full max-w-3xl mx-auto px-6 py-16" data-testid="join-page" dir="ltr" lang={lang}>
        <LangToggle lang={lang} setLang={setLang} />
        <p className="overline text-accent mb-3 flex items-center gap-2"><Scale size={14} /> {L.over}</p>
        <h1 className="font-serif font-bold text-4xl sm:text-5xl tracking-tighter mb-4" data-testid="join-heading">{L.h1(areaText(lang, area))}</h1>
        <p className="text-muted-foreground mb-10">{L.intro}</p>
        {error && <p className="text-muted-foreground" data-testid="join-error">{L.invalid} <Link to="/for-attorneys" className="text-accent underline">{L.goFor}</Link></p>}
        {!error && !info && <p className="text-muted-foreground animate-pulse">{L.loading}</p>}
        {done?.kind === "claim" && <Done title={L.checkH} body={L.checkB} L={L} offer={offer(done.county)} />}
        {done?.kind === "new" && <Done title={L.sentH} body={L.sentB} L={L} offer={offer(done.county)} />}
        {!done && info && (joined
          ? <Done title={L.inH} body={L.inB} L={L} offer={offer(info.county || "")} />
          : reviewing ? <Done title={L.revH} body={L.revB} L={L} offer={offer(info.county || "")} />
          : info.attorney ? <ClaimForm a={info.attorney} email={info.email} area={area} L={L} lang={lang} onDone={(county) => setDone({ kind: "claim", county })} />
            : <NewListingForm email={info.email} area={area} L={L} lang={lang} onDone={(county) => setDone({ kind: "new", county })} />)}
      </main>
      <Footer />
    </div>
  );
}
